<script setup lang="ts">
import InstallerProfile from "@/components/installer/InstallerProfile.vue";
import InlineLoader from "@/components/Loaders/InlineLoader.vue";
import SecondaryPageHeader from "@/components/PageHeader/SecondaryPageHeader.vue";
import InstallersSidemenu from "@/components/Sidebar/installer/InstallersSidemenu.vue";
import MainLayout from "@/layouts/MainLayout.vue";
import { useUserStore } from "@/stores/user";
import { InstallerDetails, Worker } from "@/types";
import client from "@/utils/axios";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

// Route
const route = useRoute();

// Store
const userStore = useUserStore();

// Refs
const workers = ref<Worker[]>([]);
const selectedWorker = ref<string | null>(null);
const isLoading = ref<boolean>(false);
const reports = ref<InstallerDetails | null>(null);

// Functions
const getAllInstallers = async () => {
  isLoading.value = true;
  try {
    const response = await client.get("/installers");
    workers.value = response.data;
  } catch (error) {
    console.log(error);
  } finally {
    isLoading.value = false;
  }
};

const getInstallerReport = async () => {
  try {
    const response = await client.get(
      `/installers/${route.params.id}/report/${route.params.reportId}?locale=${currentLanguage.value}`
    );
    reports.value = response.data;
  } catch (error) {
    console.log(error);
  }
};

const handleSelectWorker = (workerId: string) => {
  if (selectedWorker.value === workerId) {
    selectedWorker.value = null;
  } else {
    selectedWorker.value = workerId;
  }
};

// Computed properties
const selectedWorkerProfile = computed(() => {
  return workers.value.find((item: Worker) => item.id === route.params.id);
});

const currentLanguage = computed(() => userStore.language);
let lastDatabaseTranslationUpdateTimestamp = computed(
  () => userStore.translationsUpdateTimestamp
);

// Watchers
watch(selectedWorkerProfile, (newValue?: Worker) => {
  if (newValue) {
    handleSelectWorker(newValue.id);
  }
});

watch(
  () => route.params.reportId,
  (newReportId) => {
    if (newReportId) {
      getInstallerReport();
    }
  },
  { immediate: true }
);

watch(
  currentLanguage,
  async (newLang: string, oldLang?: string) => {
    if (newLang && oldLang && newLang !== oldLang) {
      await getInstallerReport();
    }
  },
  { immediate: true }
);

watch(
  lastDatabaseTranslationUpdateTimestamp,
  async (newTimestamp: number, oldTimestamp?: number) => {
    if (newTimestamp && oldTimestamp && newTimestamp !== oldTimestamp) {
      await getInstallerReport();
    }
  },
  { immediate: true }
);

// Lifecycle hooks
onMounted(() => {
  getAllInstallers();
});
</script>

<template>
  <MainLayout>
    <SecondaryPageHeader
      :is-installer-profile="true"
      :is-installer-report="true"
      :selected-worker-profile="selectedWorkerProfile"
      :bonus-reports="reports?.bonusReports"
    />
    <div class="w-full h-[calc(100vh-52px)] flex">
      <div
        class="w-[190px] mt-10 flex flex-col items-center gap-3"
        v-if="isLoading"
      >
        <InlineLoader />
        <InlineLoader />
        <InlineLoader />
        <InlineLoader />
      </div>
      <div class="min-w-[190px] w-[190px]" v-else>
        <InstallersSidemenu
          v-if="reports"
          :workers="workers"
          :installer-details="reports"
          @select-worker="handleSelectWorker"
        />
      </div>
      <div class="w-full">
        <InstallerProfile
          v-if="!isLoading"
          :worker-name="reports?.worker?.fullname"
          :worker-role="reports?.worker?.role"
          :worker-image="reports?.worker?.worker_image"
          :bonus="reports?.report?.bonus"
          :completed-termins="reports?.report?.completedTermins"
          :completed-termins-percentage="reports?.report?.completedPercentage"
          :termins="reports?.report?.termins"
          :is-report="true"
        />
      </div>
    </div>
  </MainLayout>
</template>
